import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Paper,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Calculate as CalcIcon,
  Home as HomeIcon,
  Build as BuildIcon,
  LocationOn as LocationIcon,
  Schedule as TimeIcon,
  TrendingUp as TrendIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as AIIcon,
  Euro as EuroIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const CostPredictor = () => {
  const [projectDetails, setProjectDetails] = useState({
    serviceType: '',
    projectSize: 'medium',
    materials: 'standard',
    complexity: 'standard',
    location: '',
    timeline: 'standard'
  });

  const [prediction, setPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Options de configuration
  const serviceTypes = [
    'Plomberie',
    'Électricité',
    'Menuiserie',
    'Peinture',
    'Maçonnerie',
    'Jardinage',
    'Rénovation',
    'Isolation',
    'Chauffage',
    'Climatisation'
  ];

  const projectSizes = [
    { value: 'small', label: 'Petit (< 20m²)', multiplier: 0.7 },
    { value: 'medium', label: 'Moyen (20-50m²)', multiplier: 1.0 },
    { value: 'large', label: 'Grand (50-100m²)', multiplier: 1.5 },
    { value: 'xlarge', label: 'Très grand (> 100m²)', multiplier: 2.0 }
  ];

  const materials = [
    { value: 'basic', label: 'Économique', multiplier: 0.8 },
    { value: 'standard', label: 'Standard', multiplier: 1.0 },
    { value: 'premium', label: 'Premium', multiplier: 1.3 },
    { value: 'luxury', label: 'Luxe', multiplier: 1.6 }
  ];

  const complexityLevels = [
    { value: 'simple', label: 'Simple', multiplier: 0.8 },
    { value: 'standard', label: 'Standard', multiplier: 1.0 },
    { value: 'complex', label: 'Complexe', multiplier: 1.3 },
    { value: 'expert', label: 'Expert', multiplier: 1.6 }
  ];

  const timelines = [
    { value: 'flexible', label: 'Flexible (+ 2 mois)', multiplier: 0.9 },
    { value: 'standard', label: 'Standard (1-2 mois)', multiplier: 1.0 },
    { value: 'urgent', label: 'Urgent (< 1 mois)', multiplier: 1.2 },
    { value: 'emergency', label: 'Urgence (< 1 semaine)', multiplier: 1.5 }
  ];

  const cities = [
    'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Nice',
    'Nantes', 'Strasbourg', 'Montpellier', 'Bordeaux', 'Lille'
  ];

  // Prédiction des coûts
  const handlePredictCost = async () => {
    if (!projectDetails.serviceType) {
      setError('Veuillez sélectionner un type de service');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/ai/predict-cost', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(projectDetails)
      });

      const data = await response.json();

      if (data.success) {
        setPrediction(data.data);
      } else {
        setError(data.message || 'Erreur lors de la prédiction');
      }
    } catch (error) {
      console.error('Erreur prédiction coût:', error);
      setError('Erreur de connexion');
    } finally {
      setIsLoading(false);
    }
  };

  // Mise à jour automatique des estimations
  useEffect(() => {
    if (projectDetails.serviceType && projectDetails.location) {
      const timer = setTimeout(() => {
        handlePredictCost();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [projectDetails]);

  // Composant de résultat de prédiction
  const PredictionResult = ({ prediction }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Card sx={{ mt: 3, border: '2px solid', borderColor: 'primary.main' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={3}>
            <AIIcon color="primary" sx={{ fontSize: 30 }} />
            <Box>
              <Typography variant="h5" color="primary">
                Estimation IA
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Prédiction basée sur l'analyse de milliers de projets similaires
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {/* Coût principal */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {prediction.estimatedCost?.toLocaleString('fr-FR')}€
                </Typography>
                <Typography variant="h6">
                  Coût Estimé
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  Confiance: {Math.round(prediction.confidence * 100)}%
                </Typography>
              </Paper>
            </Grid>

            {/* Fourchette de prix */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, bgcolor: 'background.default' }}>
                <Typography variant="h6" gutterBottom>
                  Fourchette de Prix
                </Typography>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Box textAlign="center">
                    <Typography variant="h5" color="success.main">
                      {prediction.priceRange?.min?.toLocaleString('fr-FR')}€
                    </Typography>
                    <Typography variant="caption">Minimum</Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="h5" color="error.main">
                      {prediction.priceRange?.max?.toLocaleString('fr-FR')}€
                    </Typography>
                    <Typography variant="caption">Maximum</Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <EuroIcon sx={{ fontSize: 16 }} />
                  <Typography variant="body2">
                    Écart: ±{prediction.variance || 15}%
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            {/* Facteurs d'influence */}
            <Grid item xs={12} md={8}>
              <Typography variant="h6" gutterBottom>
                <TrendIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Facteurs d'Influence
              </Typography>
              <List>
                {prediction.factors?.map((factor, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon>
                      {factor.impact > 0 ? (
                        <TrendIcon color="error" />
                      ) : (
                        <TrendIcon color="success" sx={{ transform: 'rotate(180deg)' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={factor.name}
                      secondary={`Impact: ${factor.impact > 0 ? '+' : ''}${factor.impact}%`}
                    />
                  </ListItem>
                ))}
              </List>
            </Grid>

            {/* Recommandations */}
            <Grid item xs={12} md={4}>
              <Typography variant="h6" gutterBottom>
                <CheckIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Recommandations
              </Typography>
              <Box display="flex" flexDirection="column" gap={1}>
                {prediction.recommendations?.map((rec, index) => (
                  <Chip
                    key={index}
                    label={rec}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>

          {/* Détails du calcul */}
          <Divider sx={{ my: 3 }} />
          
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                Détails du Calcul IA
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Base de Calcul:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Coût de base"
                        secondary={`${prediction.breakdown?.baseCost?.toLocaleString('fr-FR')}€`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Matériaux"
                        secondary={`${prediction.breakdown?.materialsCost?.toLocaleString('fr-FR')}€`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Main d'œuvre"
                        secondary={`${prediction.breakdown?.laborCost?.toLocaleString('fr-FR')}€`}
                      />
                    </ListItem>
                  </List>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" gutterBottom>
                    Multiplicateurs:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Localisation"
                        secondary={`×${prediction.multipliers?.location || 1.0}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Complexité"
                        secondary={`×${prediction.multipliers?.complexity || 1.0}`}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Urgence"
                        secondary={`×${prediction.multipliers?.urgency || 1.0}`}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>
    </motion.div>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          <CalcIcon sx={{ mr: 2, verticalAlign: 'middle', fontSize: 40 }} />
          Calculateur de Coûts IA
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Obtenez une estimation précise basée sur l'intelligence artificielle
        </Typography>
      </Box>

      {/* Formulaire de projet */}
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            {/* Type de service */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Type de Service</InputLabel>
                <Select
                  value={projectDetails.serviceType}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    serviceType: e.target.value
                  }))}
                  label="Type de Service"
                >
                  {serviceTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      <BuildIcon sx={{ mr: 1, fontSize: 20 }} />
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Localisation */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Localisation</InputLabel>
                <Select
                  value={projectDetails.location}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    location: e.target.value
                  }))}
                  label="Localisation"
                >
                  {cities.map((city) => (
                    <MenuItem key={city} value={city}>
                      <LocationIcon sx={{ mr: 1, fontSize: 20 }} />
                      {city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Taille du projet */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Taille du Projet</InputLabel>
                <Select
                  value={projectDetails.projectSize}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    projectSize: e.target.value
                  }))}
                  label="Taille du Projet"
                >
                  {projectSizes.map((size) => (
                    <MenuItem key={size.value} value={size.value}>
                      <HomeIcon sx={{ mr: 1, fontSize: 20 }} />
                      {size.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Délai */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Délai Souhaité</InputLabel>
                <Select
                  value={projectDetails.timeline}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    timeline: e.target.value
                  }))}
                  label="Délai Souhaité"
                >
                  {timelines.map((timeline) => (
                    <MenuItem key={timeline.value} value={timeline.value}>
                      <TimeIcon sx={{ mr: 1, fontSize: 20 }} />
                      {timeline.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Qualité des matériaux */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Qualité Matériaux</InputLabel>
                <Select
                  value={projectDetails.materials}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    materials: e.target.value
                  }))}
                  label="Qualité Matériaux"
                >
                  {materials.map((material) => (
                    <MenuItem key={material.value} value={material.value}>
                      {material.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Complexité */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Complexité</InputLabel>
                <Select
                  value={projectDetails.complexity}
                  onChange={(e) => setProjectDetails(prev => ({
                    ...prev,
                    complexity: e.target.value
                  }))}
                  label="Complexité"
                >
                  {complexityLevels.map((level) => (
                    <MenuItem key={level.value} value={level.value}>
                      {level.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Bouton de calcul */}
            <Grid item xs={12}>
              <Box textAlign="center" mt={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handlePredictCost}
                  disabled={isLoading || !projectDetails.serviceType}
                  startIcon={isLoading ? <CircularProgress size={20} /> : <AIIcon />}
                  sx={{
                    minWidth: 200,
                    height: 50,
                    background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                  }}
                >
                  {isLoading ? 'Calcul en cours...' : 'Calculer avec IA'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Erreur */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      )}

      {/* Résultat de la prédiction */}
      {prediction && !isLoading && (
        <PredictionResult prediction={prediction} />
      )}

      {/* Informations sur l'IA */}
      <Card sx={{ mt: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <InfoIcon />
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Comment fonctionne notre IA ?
              </Typography>
              <Typography variant="body2">
                Notre algorithme analyse des milliers de projets similaires, les tendances du marché,
                les spécificités régionales et les facteurs saisonniers pour vous fournir l'estimation
                la plus précise possible. La confiance indique la fiabilité de la prédiction.
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CostPredictor;